import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import type {
  BillGetOneResponse,
  BillUpdateRequestBody,
} from "@/services/core-api/types"
import { useRenameQueryResult } from "@/services/utils"
import { createQueryKey } from "@/services/utils/createQueryKey"
import { useInvalidateQueries } from "@/services/utils/useInvalidateQueries"
import type { HttpError } from "@/utils/api"
import { httpClient, toHttpError } from "@/utils/api"
import { useMutation } from "@tanstack/react-query"
import type { UseMutationOptions } from "@tanstack/react-query"

export interface UpdateBillInput {
  billId: number
  organizationId: Organization["id"]
  updates: BillUpdateRequestBody
}

export const useUpdateBill = (
  options?: UseMutationOptions<BillGetOneResponse, HttpError, UpdateBillInput>
) => {
  const { invalidateQueries } = useInvalidateQueries()

  const mutation = useMutation<BillGetOneResponse, HttpError, UpdateBillInput>({
    ...options,
    meta: { ...options?.meta, handlesOwnErrors: true },
    mutationFn: async ({
      billId,
      organizationId,
      updates,
    }: UpdateBillInput) => {
      try {
        const response = await httpClient.patch<BillGetOneResponse>(
          `/api/v1/organizations/${organizationId}/bills/${billId}`,
          updates
        )
        return response.data
      } catch (error: unknown) {
        throw toHttpError(error)
      }
    },
    onError: (...args) => {
      options?.onError?.(...args)
    },
    onSuccess: (data, variables, ...rest) => {
      invalidateQueries([
        createQueryKey(ApiQueryName.Bill, "getOne"),
        createQueryKey(ApiQueryName.Bills, "getMany"),
        createQueryKey(ApiQueryName.BillPeriods, "getMany"),
        createQueryKey(ApiQueryName.BillPeriod, "getOne"),
        createQueryKey(
          ApiQueryName.BillLineItems,
          "getMany",
          String(variables.billId)
        ),
      ])

      options?.onSuccess?.(data, variables, ...rest)
    },
  })

  return useRenameQueryResult(mutation, "updateBill")
}
